import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { z } from 'zod';
import { useLocale } from '../../app/hooks';
import { useServiceStatus, useSignUp } from '../../api/queries';
import { Alert, Button, Field, Input, PasswordInput } from '../../components/ui';
import { errorMessage } from '../../i18n/labels';
import { AuthLayout, TermsNotice } from './AuthLayout';
import { fieldError } from './fieldError';
import { OAuthButtons, OrDivider } from './OAuthButtons';
import { signUpSchema } from './schemas';
import { CheckEmail } from './VerifyEmailPage';

export function SignUpPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const signUp = useSignUp();
  const service = useServiceStatus();
  const form = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { displayName: '', email: '', password: '' },
  });
  const onSubmit = form.handleSubmit((v) =>
    signUp.mutate({
      email: v.email,
      password: v.password,
      locale,
      ...(v.displayName ? { displayName: v.displayName } : {}),
    }),
  );

  if (signUp.isSuccess) {
    return (
      <AuthLayout title={t('auth.checkEmailTitle')}>
        <CheckEmail email={form.getValues('email')} />
      </AuthLayout>
    );
  }

  const footer = (
    <>
      {t('auth.haveAccount')}{' '}
      <Link to="/login" className="link">
        {t('auth.signIn')}
      </Link>
    </>
  );

  // FR-ADM-06: staff paused new accounts; sign-in is unaffected.
  if (service.data?.signupsOpen === false) {
    return (
      <AuthLayout title={t('auth.signUpTitle')} footer={footer}>
        <Alert tone="warning" title={t('auth.signupsPausedTitle')}>
          {t('auth.signupsPaused')}
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.signUpTitle')} subtitle={t('auth.signUpSubtitle')} footer={footer}>
      {signUp.error && (
        <Alert tone="danger" className="mb-4">
          {errorMessage(t, signUp.error)}
        </Alert>
      )}
      <OAuthButtons />
      <OrDivider />
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field label={t('auth.displayName')} hint={t('common.optional')}>
          {(p) => <Input autoComplete="name" {...p} {...form.register('displayName')} />}
        </Field>
        <Field label={t('auth.email')} error={fieldError(t, form.formState.errors.email?.message)}>
          {(p) => <Input type="email" autoComplete="email" {...p} {...form.register('email')} />}
        </Field>
        <Field
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          error={fieldError(t, form.formState.errors.password?.message)}
        >
          {(p) => (
            <PasswordInput autoComplete="new-password" {...p} {...form.register('password')} />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={signUp.isPending}>
          {t('auth.createAccount')}
        </Button>
      </form>
      <TermsNotice />
    </AuthLayout>
  );
}
